import type { FunctionComponent } from "react";
import cx from "clsx";
import wcc_logo from "../../../assets/images/wcc_logo_white.png";
import social_twitter from "../../../assets/images/westminster-social-twitter.png";
import social_instagram from "../../../assets/images/westminster-social-instagram.png";
import social_facebook from "../../../assets/images/westminster-social-facebook.png";
import social_linkedin from "../../../assets/images/westminster-social-linkedin.png";

import styles from "./styles.module.css";

const Footer: FunctionComponent = () => (
  <footer className={cx(styles.footer, "p-4 lg:p-2 lg:pb-8")}>
    <div className="container">
      <div
        className={cx(
          styles.feedbackLink,
          "w-full -translate-y-8 rounded-[0.45rem] bg-sunglow px-[20px] py-[5px] lg:-translate-y-6",
        )}
      >
        <a
          href="https://www.westminster.gov.uk/form/page-feedback "
          className="rounded-[4.5px] leading-[1.2] text-text underline outline-0 hover:text-brand-dark focus:bg-text focus:text-text-inverse"
        >
          Is there anything wrong with this page?
        </a>
      </div>

      <nav
        className={cx(styles.topLinks, "flex flex-col text-left")}
        aria-label="Council information"
      >
        <div className="mb-[8.5px]">
          <a
            href="https://www.westminster.gov.uk/all-services"
            className={styles.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            All council services{" "}
            <span className="sr-only">(link opens new window)</span>
          </a>
        </div>
        <div className="mb-[8.5px]">
          <a
            href="https://www.westminster.gov.uk/events"
            className={styles.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            Events <span className="sr-only">(link opens new window)</span>
          </a>
        </div>
        <div className="mb-[8.5px]">
          <a
            href="https://www.westminster.gov.uk/news"
            className={styles.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            News <span className="sr-only">(link opens new window)</span>
          </a>
        </div>
        <div className="mb-[8.5px]">
          <a
            href="https://www.westminster.gov.uk/jobs"
            className={styles.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            Jobs <span className="sr-only">(link opens new window)</span>
          </a>
        </div>
      </nav>

      <a
        href="https://www.westminster.gov.uk/"
        className={cx(styles.logo, styles.linkIcon)}
        rel="noopener noreferrer"
        target="_blank"
      >
        <img
          className="wcc-image"
          src={wcc_logo}
          alt="MyWestminster dashboard (footer)"
        />
      </a>

      <nav
        className={cx(styles.socialMedia, "mt-4 flex gap-x-3")}
        aria-label="Social media"
      >
        <a
          href="https://en-gb.facebook.com/CityWestminster/"
          className={styles.linkIcon}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={social_facebook} alt="Social Icon - Facebook" />
        </a>

        <a
          href="https://www.instagram.com/citywestminster"
          className={styles.linkIcon}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={social_instagram} alt="Social Icon - Instagram" />
        </a>

        <a
          href="https://twitter.com/CityWestminster"
          className={styles.linkIcon}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={social_twitter} alt="Social Icon - Twitter" />
        </a>

        <a
          href="https://www.linkedin.com/company/westminster-city-council"
          className={styles.linkIcon}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={social_linkedin} alt="Social Icon - LinkedIn" />
        </a>
      </nav>

      <div
        className={cx(
          "my-4 border-b-2 border-[#ccc] opacity-10",
          styles.separator,
        )}
      />

      <nav
        className={cx(
          styles.bottomLinks,
          "flex flex-col flex-wrap gap-x-[17px] gap-y-[8.5px] lg:flex-row lg:justify-center",
        )}
        aria-label="More council information"
      >
        <a
          href="https://www.westminster.gov.uk/accessibility"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Accessibility <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/data-protection/privacy"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Cookies <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/data-protection"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Data protection{" "}
          <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/data-protection/freedom-information"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Freedom of information{" "}
          <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/sites/default/files/media/documents/WCC-PayGapReport2022-4.pdf"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Gender pay gap{" "}
          <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/modern-slavery-statement"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Modern Slavery{" "}
          <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/terms-and-conditions"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Terms & conditions{" "}
          <span className="sr-only">(link opens new window)</span>
        </a>
        <a
          href="https://www.westminster.gov.uk/about-council/contact-us"
          className={styles.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Contact us <span className="sr-only">(link opens new window)</span>
        </a>
      </nav>

      <div
        className={cx(
          "mb-5 mt-4 border-b-2 border-[#ccc] opacity-10 lg:hidden",
          styles.separatorTwo,
        )}
      />
    </div>
  </footer>
);

export default Footer;
